import { cva, type VariantProps } from "class-variance-authority";

/**
 * Inline-text link style — default color inherits from the surrounding text
 * (no `text-*` token applied), so a link inside a `foreground-base` paragraph
 * reads as foreground-base, a link inside a `foreground-neutral` paragraph
 * reads as foreground-neutral, etc. Hover / active / aria-selected flip the
 * color to ember.
 *
 * Underline behavior mirrors Button's `interaction="underline"` (see
 * `button.tsx`): underline is always visible at offset 2px, lifts to
 * offset 4px on hover, and stays at 2px on active and aria-selected
 * (active uses a faster 100ms duration so the press reads as decisive).
 * Color and underline-offset both transition for 200ms ease-out so the
 * lift feels smooth rather than abrupt.
 *
 * Foreground tokens shared with `button-icon`'s `transparent` variant
 * (Figma node 1947:10030) — inline-link styling is the same color treatment
 * as a transparent icon button.
 *
 * Usage with `<Text render={<a/>}>` (preserves Text typography control):
 *
 * ```tsx
 * <Text
 *     variant="small"
 *     render={<a href="..." className={linkVariants()} />}
 * >
 *   {label}
 * </Text>
 * ```
 *
 * Or directly on an `<a>`:
 *
 * ```tsx
 * <a href="..." className={linkVariants()}>{label}</a>
 * ```
 */
const linkVariants = cva(
    [
        "cursor-pointer underline underline-offset-2 decoration-[1px]",
        "transition-[color,text-underline-offset] duration-200 ease-out",
        "hover:text-button-icon-transparent-foreground-hover hover:underline-offset-4",
        "active:text-button-icon-transparent-foreground-hover active:underline-offset-2 active:duration-100",
        "aria-selected:text-button-icon-transparent-foreground-hover aria-selected:underline-offset-2"
    ].join(" "),
    {
        variants: {},
        defaultVariants: {}
    }
);

type LinkVariants = VariantProps<typeof linkVariants>;

export { linkVariants };
export type { LinkVariants };
